<template>
    <div class="absolute top-0 left-0 h-full w-full z-20 popup-glass flex items-center justify-center px-2" v-if="conformationPopupIsOpen">
        <div class="w-full max-w-[480px] bg-white px-4 py-6 rounded-md shadow-md">
            <h3 class="font-sans font-medium text-red-400 text-lg text-center">Delete Donor Benefit</h3>
            <p class="font-minion text-[#00423D] font-semibold text-center mt-3">Are you sure you want to delete this benefit?</p>
            <div class="flex items-center justify-center gap-5 mt-6">
                <button class="px-6 py-1 rounded-md text-gray-400" @click="onCancelRemove">Cancel</button>
                <button class="bg-red-500 px-6 py-1 rounded-md text-white" @click="onConformRemove">Delete</button>
            </div>
        </div>
    </div>
    <div class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]">
        <div class=" text-[#414141] font-sans">
            <h3 class="text-[1.25rem] leading-[1.75rem] mb-[1rem] font-semibold">Donor Recognition and Benefit Structure</h3>
            <p class="text-[14px] leading-[22px] mb-[0.5rem]">Offering benefits and recognition to your donors is a great way to steward them after they have made gifts to your organization. The more your benefits and recognition integrate your donors into the work of your organization, the better. More intimate and exclusive benefits should be reserved for higher level donors; More accessible and cost effective benefits should be reserved for smaller level donors. The objective is to keep donors engaged with your organization's work and progress.</p>
            
            <p class="text-[14px] leading-[22px] mb-[0.5rem]">Examples of benefits and recognition include: Private dinners with organizational leaders, field trips, naming opportunities, newsletters, annual report listings, etc.</p>

            <p class="text-[14px] leading-[22px] mt-5 mb-[0.5rem]"><span class="font-bold text-[#00423D]">Instructions:</span> Below, you will find a framework to help you build out your organization's Stewardship structure. In the left column, insert the benefit or recognition you will offer your donors at various the gift levels listed in the top row. Then, click in the corresponding box under the gift level that will receive each benefit or recognition. Your final stewardship chart will be inserted into your case for support.</p>
        </div>

        <template v-if="isLoading">
            <div class="w-full h-full flex items-center justify-center">
                <div class="">
                    <ActivityIndicator />
                </div>
            </div>
        </template>
        <template v-else>
            <!-- Table Container -->
            <div class="mt-6 overflow-x-auto">
                <table class="w-full border border-gray-300 border-collapse table-fixed">
                    <thead>
                        <tr class="bg-[#00423D] text-white font-minion font-normal border border-[#00423D]">
                            <th class="font-minion font-bold border text-[14px] border-gray-300 border-l-[#00423D] border-t-[#00423D] p-2 text-left w-[40%]">
                                Donor Benefit
                            </th>
                            <th class="px-1 font-['Open_Sans'] text-[11px] w-[10%]" v-for="(row, id) in tableStateStore" :key="id">
                                <span v-if="row.giftAmount != 'Various (<$25k)'">$ </span>
                                {{ row.giftAmount.toLocaleString() }}
                            </th>
                            <th class="font-normal border border-gray-300 border-r-[#00423D] border-t-[#00423D] p-2 font-['Open_Sans'] w-[5%]"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="border border-gray-300 even:bg-[#DDEDEA] odd:bg-white" v-for="(mainRow, index) in tableState.rows" :key="index">
                            <td class="border border-gray-300 p-1 w-[40%]">
                                <textarea
                                    class="p-1 bg-transparent outline-none border-none w-full text-[#00423D] font-['Open_Sans'] auto-height text-[14px]"
                                    v-model="mainRow.benefit_name"
                                    rows="1"
                                    placeholder="Write Here..."
                                ></textarea>
                            </td>

                            <td v-for="(row, idx) in tableStateStore" :key="idx" class="border border-gray-300">
                                <input
                                    class="hidden"
                                    type="checkbox"
                                    :id="`prospect-${index}-${row.giftAmount}`"
                                    v-model="mainRow.prospect[row.giftAmount]"
                                />
                                <label
                                    :for="`prospect-${index}-${row.giftAmount}`"
                                    class="cursor-pointer w-full py-1.5 flex items-center justify-center"
                                >
                                    <div v-if="mainRow.prospect[row.giftAmount]">
                                        <svg class="h-6 w-6" width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <mask id="mask0_1304_29344" style="mask-type: alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="48" height="48">
                                                <rect width="48" height="48" fill="#D9D9D9" />
                                            </mask>
                                            <g mask="url(#mask0_1304_29344)">
                                                <path d="M18.9002 35.7002L7.7002 24.5002L9.8502 22.3502L18.9002 31.4002L38.1002 12.2002L40.2502 14.3502L18.9002 35.7002Z" class="fill-primary-400" />
                                            </g>
                                        </svg>
                                    </div>
                                    <div v-else>
                                        <div class="h-6 w-6"></div>
                                    </div>
                                </label>
                            </td>

                            <td class="border border-gray-300 p-1">
                                <button class="p-1 w-full flex items-center justify-center" @click="onRemoveRow(index)" :data-point-id="index">
                                    <svg class="w-6 h-6 pointer-events-none" width="49" height="49" viewBox="0 0 49 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <mask id="mask0_1675_29390" style="mask-type: alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="49" height="49">
                                            <rect x="0.0527344" y="0.480957" width="48" height="48" fill="#D9D9D9" />
                                        </mask>
                                        <g mask="url(#mask0_1675_29390)">
                                            <path d="M13.1027 42.481C12.2694 42.481 11.5611 42.1893 10.9777 41.606C10.3944 41.0226 10.1027 40.3143 10.1027 39.481V10.981H8.05273V7.98096H17.4527V6.48096H30.6527V7.98096H40.0527V10.981H38.0027V39.481C38.0027 40.281 37.7027 40.981 37.1027 41.581C36.5027 42.181 35.8027 42.481 35.0027 42.481H13.1027ZM35.0027 10.981H13.1027V39.481H35.0027V10.981ZM18.4027 35.181H21.4027V15.231H18.4027V35.181ZM26.7027 35.181H29.7027V15.231H26.7027V35.181Z" class="fill-gray-400" />
                                        </g>
                                    </svg>
                                </button>
                            </td>
                        </tr>
                        <tr>
                            <td :colspan="tableStateStore.length + 2">
                                <button class="py-1.5 w-full flex items-center text-[#00423D] font-['Open_Sans']" @click="onAddRow">
                                    <svg class="h-6 w-6" width="49" height="49" viewBox="0 0 49 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <mask id="mask0_1409_29353" style="mask-type: alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="49" height="49">
                                            <rect x="0.322266" y="0.00683594" width="48" height="48" fill="#D9D9D9" />
                                        </mask>
                                        <g mask="url(#mask0_1409_29353)">
                                            <path d="M24.3223 38.0068C23.8889 38.0068 23.5306 37.8652 23.2473 37.5818C22.9639 37.2985 22.8223 36.9402 22.8223 36.5068V25.5068H11.8223C11.3889 25.5068 11.0306 25.3652 10.7473 25.0818C10.4639 24.7985 10.3223 24.4402 10.3223 24.0068C10.3223 23.5735 10.4639 23.2152 10.7473 22.9318C11.0306 22.6485 11.3889 22.5068 11.8223 22.5068H22.8223V11.5068C22.8223 11.0735 22.9639 10.7152 23.2473 10.4318C23.5306 10.1485 23.8889 10.0068 24.3223 10.0068C24.7556 10.0068 25.1139 10.1485 25.3973 10.4318C25.6806 10.7152 25.8223 11.0735 25.8223 11.5068V22.5068H36.8223C37.2556 22.5068 37.6139 22.6485 37.8973 22.9318C38.1806 23.2152 38.3223 23.5735 38.3223 24.0068C38.3223 24.4402 38.1806 24.7985 37.8973 25.0818C37.6139 25.3652 37.2556 25.5068 36.8223 25.5068H25.8223V36.5068C25.8223 36.9402 25.6806 37.2985 25.3973 37.5818C25.1139 37.8652 24.7556 38.0068 24.3223 38.0068Z" class="fill-[#00423D]" />
                                        </g>
                                    </svg>
                                    <span> Add New Row </span>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p v-if="benefitNameError" class="text-red-500 text-sm mt-2">{{ benefitNameError }}</p>
            </div>
        </template>
    </div>
</template>

<script setup>
import { ref, onMounted, reactive, computed } from "vue";
import { useRouter } from "vue-router";
const router = useRouter();

// Components
import ActivityIndicator from "@/components/ActivityIndicator.vue";

// Global
import global from "@/global";

// Pinia Store: Auth
import { useAuthStore } from "@/stores/auth";
import { useUserProjectsStore } from "@/stores/projects";
import { useRoutesManagement } from "@/stores/routesManagement";
import { useStepValidation } from "@/composables/useStepValidation";

const authStore = useAuthStore();
const projectsStore = useUserProjectsStore();
const routesManagementStore = useRoutesManagement();

// State
const isLoading = ref(true);
const conformationPopupIsOpen = ref(false);
const conformDeletionState = ref(null);
const tableStateStore = ref([]);
const giftPyramidTableState = reactive({
    data: [],
});
const targetAllocation = ref([]);
const errorMessage = ref("");
const benefitNameError = ref("");

const tableState = reactive({
    headers: [],
    rows: [],
});

// Function to clear error messages after 5 seconds
function setErrorWithTimeout(errorRef, message) {
    errorRef.value = message;
    setTimeout(() => {
        errorRef.value = "";
    }, 5000);
}

// Validation function for the useStepValidation composable
const validateStep = async () => {
    try {
        // Check if any benefit name is empty
        const hasEmptyBenefitName = tableState.rows.some(row => !row.benefit_name.trim());
        if (hasEmptyBenefitName) {
            setErrorWithTimeout(benefitNameError, "Please fill in all benefit names before proceeding");
            return false;
        }

        // Save all stewardship data
        const success = await saveUserAnswer();
        if (!success) {
            setErrorWithTimeout(errorMessage, "Please ensure all stewardship data is saved before proceeding.");
            return false;
        }
        return true;
    } catch (error) {
        console.error("Failed to save stewardship data:", error);
        setErrorWithTimeout(errorMessage, "Please ensure your stewardship data is saved before proceeding.");
        return false;
    }
};

// Use the step validation composable
useStepValidation({
    initialValidState: false,
    validateFn: validateStep,
});

// Functions
function onAddRow() {
    const lastRow = tableState.rows[tableState.rows.length - 1];
    if (lastRow && !lastRow.benefit_name.trim()) {
        setErrorWithTimeout(benefitNameError, "Benefit name is required");
        return;
    }
    benefitNameError.value = "";
    
    // Create prospect object dynamically based on tableStateStore
    const prospect = {};
    tableStateStore.value.forEach(row => {
        prospect[row.giftAmount] = false;
    });
    
    tableState.rows.push({
        benefit_name: "",
        prospect: prospect,
    });
}

function onRemoveRow(id) {
    conformationPopupIsOpen.value = true;
    conformDeletionState.value = id;
}

function onCancelRemove() {
    conformationPopupIsOpen.value = false;
}

async function onConformRemove() {
    let pointId = conformDeletionState.value;
    try {
        if (tableState.rows[pointId].id) {
            const res = await fetch(`${global.API_URL}/stewardships/${tableState.rows[pointId].id}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${authStore.user.accessToken}`,
                },
            });

            if (!res.ok) {
                throw new Error(`HTTP error! Status: ${res.status}`);
            }

            const data = await res.json();
            if (!data.status) {
                throw new Error(data.error || "Failed to delete stewardship");
            }
        }

        tableState.rows = tableState.rows.filter((point, idx) => idx != pointId);
        conformationPopupIsOpen.value = false;
        conformDeletionState.value = null;
    } catch (error) {
        console.error("Failed to delete stewardship:", error);
        errorMessage.value = "Failed to delete the stewardship. Please try again.";
    }
}

function setProperTargetAllocation(totalFundraisingGoal) {
    if (totalFundraisingGoal >= 20000000) {
        targetAllocation.value = [0.5, 0.2, 0.15, 0.15, 0.0, 0.0];
    } else if (totalFundraisingGoal >= 17500000) {
        targetAllocation.value = [0.4, 0.15, 0.15, 0.3, 0.0, 0.0];
    } else if (totalFundraisingGoal >= 15000000) {
        targetAllocation.value = [0.15, 0.2, 0.25, 0.4, 0.0, 0.0];
    } else if (totalFundraisingGoal >= 12500000) {
        targetAllocation.value = [0.15, 0.2, 0.2, 0.45, 0.0, 0.0];
    } else if (totalFundraisingGoal >= 10000000) {
        targetAllocation.value = [0.1, 0.15, 0.15, 0.6, 0.0, 0.0];
    } else if (totalFundraisingGoal >= 7500000) {
        targetAllocation.value = [0.0, 0.3, 0.25, 0.2, 0.25, 0.0];
    } else if (totalFundraisingGoal >= 5000000) {
        targetAllocation.value = [0.0, 0.2, 0.27, 0.17, 0.35, 0.0];
    } else if (totalFundraisingGoal >= 2500000) {
        targetAllocation.value = [0.0, 0.0, 0.0, 0.4, 0.375, 0.225];
    } else if (totalFundraisingGoal >= 1000000) {
        targetAllocation.value = [0.0, 0.0, 0.0, 0.45, 0.25, 0.3];
    } else if (totalFundraisingGoal > 500000) {
        targetAllocation.value = [0.0, 0.0, 0.0, 0.1, 0.3, 0.6];
    } else if (totalFundraisingGoal == 500000) {
        targetAllocation.value = [0.0, 0.0, 0.0, 0.5, 0.4, 0.1];
    }
}

function buildTableData(prospectRatio, totalFundraisingGoal, prospectNamesArr) {
    let giftAmounts = [5000000, 1000000, 500000, 250000, 100000, 25000];

    let tempTableData = [];
    let totalPercentage = 0;
    let calculatedTotalFundraisingGoal = 0;

    giftAmounts.forEach((ga, idx) => {
        let TargetAllocationTemp = targetAllocation.value[idx];

        let targetAllocationValue = 0;
        let numberOfGiftsNeeded = 0;
        let amountByGiftLevel = 0;

        let calculatedFundraisingAllocationPercentage = 0;

        if (totalPercentage + TargetAllocationTemp * 100 <= 100) {
            targetAllocationValue = totalFundraisingGoal * TargetAllocationTemp;

            numberOfGiftsNeeded = Math.ceil(targetAllocationValue / ga);

            amountByGiftLevel = numberOfGiftsNeeded * ga;

            calculatedFundraisingAllocationPercentage = +((amountByGiftLevel * 100) / totalFundraisingGoal).toFixed(1);

            calculatedTotalFundraisingGoal += amountByGiftLevel;
            totalPercentage += calculatedFundraisingAllocationPercentage;
        } else {
            TargetAllocationTemp = (100 - totalPercentage) / 100;

            targetAllocationValue = totalFundraisingGoal * TargetAllocationTemp;

            numberOfGiftsNeeded = Math.ceil(targetAllocationValue / ga);

            amountByGiftLevel = numberOfGiftsNeeded * ga;

            calculatedFundraisingAllocationPercentage = +((amountByGiftLevel * 100) / totalFundraisingGoal).toFixed(1);

            if (amountByGiftLevel + calculatedTotalFundraisingGoal >= totalFundraisingGoal) {
                numberOfGiftsNeeded = Math.ceil(targetAllocationValue / ga) - 1;
                amountByGiftLevel = numberOfGiftsNeeded * ga;
                calculatedFundraisingAllocationPercentage = +((amountByGiftLevel * 100) / totalFundraisingGoal).toFixed(1);
            }

            calculatedTotalFundraisingGoal += amountByGiftLevel;
            totalPercentage += calculatedFundraisingAllocationPercentage;
        }

        tempTableData.push({
            giftAmount: ga,
            numberOfGiftsNeeded: numberOfGiftsNeeded,
            numberOfProspectsNeeded: numberOfGiftsNeeded * prospectRatio,
            amountByGiftLevel: amountByGiftLevel,
            cumulativeGiftAmount: tempTableData[idx - 1] ? amountByGiftLevel + tempTableData[idx - 1].cumulativeGiftAmount : amountByGiftLevel,
            fundingAllocation: calculatedFundraisingAllocationPercentage,
            prospectNames: {
                names: "",
            },
        });
    });

    tempTableData.push({
        giftAmount: "Various",
        numberOfGiftsNeeded: "-",
        numberOfProspectsNeeded: "-",
        amountByGiftLevel: totalFundraisingGoal - calculatedTotalFundraisingGoal,
        cumulativeGiftAmount: totalFundraisingGoal,
        fundingAllocation: parseFloat((100 - totalPercentage).toFixed(1)),
        prospectNames: {
            names: "",
        },
    });

    tempTableData.forEach((row, idx) => {
        row.prospectNames = prospectNamesArr[idx];
    });

    giftPyramidTableState.data = tempTableData;
}

function buildTableHeaders(prospectRatio, totalFundraisingGoal, prospectNames) {
    buildTableData(prospectRatio, roundToNearest100000(totalFundraisingGoal), prospectNames);
    let tempHeaders = [];
    giftPyramidTableState.data.forEach((di) => {
        let obj = {};
        obj.name = "";
        obj.value = `${di.amountByGiftLevel}`;
        obj.order = di.prospectNames?.order;
        tempHeaders.push(obj);
    });
    tableState.headers = tempHeaders.sort((a, b) => a.order - b.order);
}

function roundToNearest100000(number) {
    return Math.ceil(number / 100000) * 100000;
}

// Getting user answer
async function getUserAnswer() {
    try {
        const res = await fetch(`${global.API_URL}/stewardships`, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authStore.user.accessToken}`,
            },
        });

        if (!res.ok) {
            throw new Error(`HTTP error! Status: ${res.status}`);
        }

        const answer = await res.json();
        
        // Handle the new response structure
        if (answer.stewardships && answer.stewardships.length === 0) {
            onAddRow();
        } else if (answer.stewardships) {
            let tempRows = [];
            answer.stewardships.forEach((ss) => {
                let obj = { ...ss.benefit, id: ss.id };
                
                // Ensure prospect object has all required gift amounts from tableStateStore
                if (tableStateStore.value.length > 0) {
                    const prospect = {};
                    tableStateStore.value.forEach(row => {
                        prospect[row.giftAmount] = obj.prospect[row.giftAmount] || false;
                    });
                    obj.prospect = prospect;
                }
                
                tempRows.push(obj);
            });
            tableState.rows = tempRows;
        }

        return answer;
    } catch (error) {
        console.error("Failed to fetch stewardships:", error);
        errorMessage.value = "Failed to load your previous stewardship data. You can still create new ones.";
        onAddRow(); // Add an empty row if we can't fetch existing data
    }
}

// Saving user answer
async function saveUserAnswer() {
    try {
        // Validate all benefit names before saving
        const hasEmptyBenefitName = tableState.rows.some(row => !row.benefit_name.trim());
        if (hasEmptyBenefitName) {
            setErrorWithTimeout(benefitNameError, "Please fill in all benefit names before saving");
            return false;
        }

        let tempSSs = [];
        tableState.rows.forEach((row) => {
            let obj = {};
            if (row.id) {
                obj.id = row.id;
            }
            obj.benefit = row;
            tempSSs.push(obj);
        });

        const res = await fetch(`${global.API_URL}/stewardships`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authStore.user.accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                stewardships: tempSSs,
            }),
        });

        if (!res.ok) {
            throw new Error(`HTTP error! Status: ${res.status}`);
        }

        const data = await res.json();
        if (!data.status) {
            throw new Error(data.error || "Failed to save stewardships");
        }

        return true;
    } catch (error) {
        console.error("Failed to save stewardships:", error);
        setErrorWithTimeout(errorMessage, "Failed to save your stewardship data. Please try again.");
        return false;
    }
}

const retrievedUserAnswer = ref(null);

async function getGiftPyramid() {
    try {
        const res = await fetch(`${global.website_URL}/api/v1/gift-pyramid`, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authStore.user.accessToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!res.ok) {
            throw new Error(`HTTP error! Status: ${res.status}`);
        }

        const data = await res.json();
        const rawLevels = JSON.parse(data.data.levels);
        
        // Filter levels to only show those with numberOfGiftsNeeded > 0
        let levels = [];
        rawLevels.forEach((ele, i) => {
            if (ele["numberOfGiftsNeeded"] > 0) {
                levels.push(ele);
            }
        });
        
        // Always add "Various" column (last item in the array)
        levels.push(rawLevels[rawLevels.length - 1]);
        
        tableStateStore.value = levels;
    } catch (error) {
        console.error("Failed to fetch gift pyramid:", error);
        errorMessage.value = "Failed to load gift pyramid data. Please try refreshing the page.";
    }
}

onMounted(async () => {
    try {
        isLoading.value = true;
        errorMessage.value = "";

        // Ensure we have the latest user data
        await authStore.fetchUserData();

        if (!authStore.user?.organization_id) {
            errorMessage.value = "Unable to load organization data. Please try refreshing the page.";
            return;
        }

        await getGiftPyramid();
        await getUserAnswer();

        localStorage.removeItem("questionFetchData");
    } catch (error) {
        console.error("Error during component initialization:", error);
        errorMessage.value = "An error occurred while loading the data. Please try refreshing the page.";
    } finally {
        isLoading.value = false;
    }
});
</script>

<style scoped>
.auto-height {
    resize: none;
    overflow: hidden;
    min-height: 24px;
    max-height: 200px;
}
</style> 